import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';

/*
 * Per-practice write-only secret storage. The server returns only
 * metadata ({ hasValue, updatedAt }) — the actual plaintext is never
 * included in any response. Setting is PUT, clearing is DELETE (or PUT
 * with an empty value).
 */

export type SecretKey = 'oa-sftp-password';

export interface SecretStatus {
    hasValue: boolean;
    updatedAt: string | null;
}

export function useSecretStatus(key: SecretKey) {
    return useQuery({
        queryKey: ['practice-secret', key] as const,
        queryFn: async (): Promise<SecretStatus> => {
            const res = await api.get<SecretStatus>(`/practice-secrets/${key}`);
            return res.data;
        },
        staleTime: 5 * 60_000,
    });
}

export function useSetSecret(key: SecretKey) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (value: string): Promise<SecretStatus> => {
            const res = await api.put<SecretStatus>(`/practice-secrets/${key}`, { value });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['practice-secret', key] }),
    });
}

export function useClearSecret(key: SecretKey) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (): Promise<void> => {
            await api.delete(`/practice-secrets/${key}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['practice-secret', key] }),
    });
}
